import {
    CodeBlockEditorDescriptor,
    CodeMirrorEditor,
    JsxComponentDescriptor,
    MDXEditor,
    MDXEditorMethods,
    codeBlockPlugin,
    codeMirrorPlugin,
    frontmatterPlugin,
    headingsPlugin,
    jsxPlugin,
    linkDialogPlugin,
    linkPlugin,
    listsPlugin,
    markdownShortcutPlugin,
    quotePlugin,
    tablePlugin,
    thematicBreakPlugin,
} from '@mdxeditor/editor'
import { FC, useEffect, useMemo, useRef } from 'react'
import { PhotoProvider } from 'react-photo-view'
import { CODE_BLOCK_LANGUAGES } from '../../../../constants/constants'
import { ReplyCitationDTO } from '../../../../swagger/dto'
import {
    getFootnoteDescriptor,
    getGoodsItemDescriptor,
    getInlineImageDescriptor,
    getTextSplitterDescriptor,
} from '../request.constants'

interface Props {
    text?: string
    citations?: ReplyCitationDTO[]
    extraJsxComponentDescriptors?: JsxComponentDescriptor[]
    contentEditableClassName?: string
}

const MarkdownEditor: FC<Props> = ({
    text = '',
    citations = [],
    extraJsxComponentDescriptors = [],
    contentEditableClassName,
}) => {
    const ref = useRef<MDXEditorMethods>(null)

    const plainTextCodeEditorDescriptor: CodeBlockEditorDescriptor = {
        match: (language, meta) => true,
        priority: -10,
        Editor: (props) => <span>{props.code}</span>,
    }

    const copyButtonCodeEditorDescriptor: CodeBlockEditorDescriptor = {
        match: (language, meta) => true,
        priority: 10,
        Editor: (props) => (
            <>
                <div style={{ position: 'relative' }}>
                    <button
                        onClick={() => navigator.clipboard.writeText(props.code.replaceAll("⁢", ""))}
                        style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            padding: '4px 8px',
                            backgroundColor: '#rgba(0, 0, 0, 0.6)',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            zIndex: 1
                        }}
                    >
                        Копировать
                    </button>
                    <CodeMirrorEditor {...props} />
                </div>
            </>
        ),
    }

    const markdown = text.replaceAll('---\n', '')

    useEffect(() => {
        if (markdown !== ref.current?.getMarkdown()) {
            ref.current?.setMarkdown(markdown)
        }
    }, [markdown])

    const plugins = useMemo(
        () => [
            headingsPlugin(),
            listsPlugin(),
            quotePlugin(),
            thematicBreakPlugin(),
            linkPlugin(),
            frontmatterPlugin(),
            linkDialogPlugin(),
            codeBlockPlugin({
                defaultCodeBlockLanguage: 'txt',
                codeBlockEditorDescriptors: [plainTextCodeEditorDescriptor, copyButtonCodeEditorDescriptor],
            }),
            codeMirrorPlugin({
                theme: 'dark',
                codeBlockLanguages: CODE_BLOCK_LANGUAGES,
            }),
            markdownShortcutPlugin(),
            tablePlugin(),
            jsxPlugin({
                jsxComponentDescriptors: [
                    getFootnoteDescriptor(citations),
                    getGoodsItemDescriptor(),
                    getTextSplitterDescriptor(),
                    getInlineImageDescriptor(),
                    ...extraJsxComponentDescriptors,
                ],
            }),
        ],
        [citations],
    )

    return (
        <PhotoProvider>
            <MDXEditor
                ref={ref}
                suppressHtmlProcessing={true}
                onError={(e) => console.log(e)}
                readOnly
                markdown={markdown}
                contentEditableClassName={`dialog-editor dialog-editor_read_only ${contentEditableClassName}`}
                plugins={plugins}
            />
        </PhotoProvider>
    )
}

export default MarkdownEditor
